import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';

import { DocumentItem } from '../../interfaces/document-item.interface';
import { DeliveryRouteCandidate } from '../../interfaces/delivery-route.interface';
import {
  DeliveryRouteSplitDialogData,
  DeliveryRouteSplitDocument,
  DocumentSplitReason,
  LineAltUnits,
} from '../../interfaces/dialog-data.interface';
import { DgNumberPipe } from '../../dg-number.pipe';
import { altUnitsOf } from '../../alt-units.util';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslationKey } from 'app/core/i18n/translation-keys';

// as const conserva el literal: sin él la clave se ensancha y t() pide parámetros
const SPLIT_REASON_KEYS = {
  WAREHOUSE: 'DOCUMENT_GENERATE.SPLIT_MODAL.REASON_WAREHOUSE',
  WITHOUT_STOCK: 'DOCUMENT_GENERATE.SPLIT_MODAL.REASON_WITHOUT_STOCK',
  DELIVERY_WINDOW: 'DOCUMENT_GENERATE.SPLIT_MODAL.REASON_DELIVERY_WINDOW',
} as const satisfies Record<DocumentSplitReason, TranslationKey>;

// Ruta de entrega de cada documento del reparto y cómo quedó repartido el pedido.
// Reemplaza al modal de candidatas: aquí no se explica por qué se descartó una ruta,
// solo se elige entre las que sí pueden entregar
@Component({
  selector: 'app-delivery-route-split-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
    MatRadioModule,
    DgNumberPipe,
    TranslatePipe,
  ],
  templateUrl: './delivery-route-split-modal.component.html',
})
export class DeliveryRouteSplitModalComponent implements OnInit, OnDestroy {
  // ruta elegida en cada documento, por índice de documento
  selectedByDocument: Record<number, string | null> = {};
  selectedIndex = 0;
  // la lista de rutas arranca plegada: con muchas candidatas tapaba la tabla de artículos
  routesExpanded = false;

  // en pantallas de poca altura el encabezado no deja ver los documentos
  shortScreen = false;
  private shortScreenSub?: Subscription;

  constructor(
    private dialogRef: MatDialogRef<DeliveryRouteSplitModalComponent>,
    private breakpointObserver: BreakpointObserver,
    private i18n: LanguageService,
    @Inject(MAT_DIALOG_DATA) public data: DeliveryRouteSplitDialogData
  ) {
    for (const document of data.documents ?? []) {
      this.selectedByDocument[document.index] = document.selectedRouteId;
    }
  }

  ngOnInit(): void {
    this.shortScreenSub = this.breakpointObserver
      .observe('(max-height: 560px)')
      .subscribe((state) => (this.shortScreen = state.matches));
  }

  ngOnDestroy(): void {
    this.shortScreenSub?.unsubscribe();
  }

  get documents(): DeliveryRouteSplitDocument[] {
    return this.data.documents ?? [];
  }

  get selected(): DeliveryRouteSplitDocument | null {
    return this.documents[this.selectedIndex] ?? null;
  }

  // con un solo documento no se nombra: la presentación queda como sin reparto
  get showsDocumentLabels(): boolean {
    return this.documents.length > 1;
  }

  select(index: number): void {
    this.selectedIndex = index;
  }

  toggleRoutes(): void {
    this.routesExpanded = !this.routesExpanded;
  }

  lineCount(document: DeliveryRouteSplitDocument): number {
    return this.purchasedItems(document).length;
  }

  // el bono no se lista aparte: acompaña a su línea de origen
  purchasedItems(document: DeliveryRouteSplitDocument): DocumentItem[] {
    return (document.items ?? []).filter((item) => !item.isBonus);
  }

  warehouseLabel(item: DocumentItem): string {
    const code = item.warehouseCode ?? '';
    const name = item.warehouseName ?? '';
    return code && name ? `${code} - ${name}` : code || name;
  }

  // la cantidad se muestra con los decimales que admite la unidad de la línea
  // conversión de la cantidad de la línea a unidad secundaria y paralela
  altUnits(item: DocumentItem): LineAltUnits {
    return altUnitsOf(item.material, item.unitMeasureId, item.quantity, this.data.umpActive !== false);
  }

  quantityText(item: DocumentItem): string {
    const decimals = item.hasAcceptedDecimals ? Number(item.decimalsAllowed ?? 0) || 0 : 0;
    const text = (Number(item.quantity) || 0).toFixed(decimals);
    return text.includes('.') ? text.replace(/\.?0+$/, '') : text;
  }

  rowTotal(item: DocumentItem): number {
    const subtotal = item.quantity * item.price - (this.data.taxIncluded ? item.isv : 0);
    return subtotal - item.discount + item.isv;
  }

  // la candidata que está elegida en el documento visible
  get selectedCandidate(): DeliveryRouteCandidate | null {
    const document = this.selected;
    if (!document) return null;
    const routeId = this.selectedByDocument[document.index];
    return document.candidates.find((candidate) => candidate._id === routeId) ?? null;
  }

  hasRoute(document: DeliveryRouteSplitDocument): boolean {
    return !!this.selectedByDocument[document.index];
  }

  routeName(document: DeliveryRouteSplitDocument): string {
    const routeId = this.selectedByDocument[document.index];
    return document.candidates.find((candidate) => candidate._id === routeId)?.name ?? '';
  }

  reasonLabel(reason: DocumentSplitReason): string {
    return this.i18n.t(SPLIT_REASON_KEYS[reason]);
  }

  get documentsWithoutRoute(): DeliveryRouteSplitDocument[] {
    return this.documents.filter((document) => !this.selectedByDocument[document.index]);
  }

  get canConfirm(): boolean {
    return !this.documentsWithoutRoute.length || this.data.canGenerateWithoutRoute;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    if (!this.canConfirm) return;
    this.dialogRef.close({ routes: this.selectedByDocument });
  }
}
